import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Routes, Route, BrowserRouter } from "react-router-dom";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Button } from "@fluentui/react-components";
import { Home } from "./pages/Home";
import { AppShell } from "./components/AppShell";
import { PageState } from "./components/PageState";
import { FunctionProvider } from "./contexts/FunctionContext";
import { PoolBrowsingProvider } from "./contexts/PoolBrowsingContext";
import { ViewPreferencesProvider } from "./contexts/ViewPreferencesContext";
import "./App.css";
import "./components/AuthRedirector.css";

const Call = lazy(() => import("./pages/Call").then(module => ({ default: module.Call })));
const CancelBuildPipelines = lazy(() => import("./pages/CancelBuildPipelines").then(module => ({ default: module.CancelBuildPipelines })));
const SelectPool = lazy(() => import("./pages/SelectPool"));
const GetSpecificPool = lazy(() => import("./pages/GetSpecificPool"));
const GetCstEntryHistory = lazy(() => import("./pages/GetCstEntryHistory"));
const AlertStatus = lazy(() => import("./pages/AlertStatus"));
const ReleaseReliability = lazy(() => import("./pages/ReleaseReliability"));
const PipelineReliability = lazy(() => import("./pages/PipelineReliability"));
const CommitHeatmap = lazy(() => import("./pages/CommitHeatmap"));
const ControlCenter = lazy(() => import("./pages/ControlCenter"));
const FunctionNotFound = lazy(() => import("./components/FunctionNotFound").then(module => ({ default: module.FunctionNotFound })));
const Return404 = lazy(() => import("./pages/Return404").then(module => ({ default: module.Return404 })));

function AuthenticatedApp() {
    const authenticated = useIsAuthenticated();
    const { instance, inProgress } = useMsal();
    const attempted = useRef(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (authenticated) {
            attempted.current = false;
        } else if (inProgress === "none" && !attempted.current) {
            attempted.current = true;
            instance.loginRedirect({ scopes: ["User.Read"] }).catch(error => {
                console.error("Unable to sign in:", error);
                setError(error);
            });
        }
    }, [authenticated, inProgress, instance]);

    if (error) return <PageState error title="Sign-in could not be completed" action={<Button onClick={() => window.location.reload()}>Try again</Button>}>{error.message}</PageState>;
    if (!authenticated) return <PageState loading title="Signing you in to EzFunc..." />;

    return (
        <BrowserRouter>
            <ViewPreferencesProvider>
            <FunctionProvider>
                <PoolBrowsingProvider>
                <AppShell>
                    <Suspense fallback={<PageState loading title="Loading workspace..." />}>
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/control-center" element={<ControlCenter />} />
                            <Route path="/pools" element={<SelectPool />} />
                            <Route path="/pools/:poolName/history/:entryId" element={<GetCstEntryHistory />} />
                            <Route path="/pools/:poolName" element={<GetSpecificPool />} />
                            <Route path="/cancelBuildPipelines" element={<CancelBuildPipelines />} />
                            <Route path="/alertstatus" element={<AlertStatus />} />
                            <Route path="/release-reliability" element={<ReleaseReliability />} />
                            <Route path="/pipeline-reliability" element={<PipelineReliability />} />
                            <Route path="/commit-heatmap" element={<CommitHeatmap />} />
                            <Route path="/function-not-found/:functionName?" element={<FunctionNotFound />} />
                            <Route path="/:functionName" element={<Call />} />
                            <Route path="*" element={<Return404 />} />
                        </Routes>
                    </Suspense>
                </AppShell>
                </PoolBrowsingProvider>
            </FunctionProvider>
            </ViewPreferencesProvider>
        </BrowserRouter>
    );
}

export default function App({ msalInstance }) {
    return <MsalProvider instance={msalInstance}><AuthenticatedApp /></MsalProvider>;
}
